import React, { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Sparkles, Paperclip, FileText } from "lucide-react";
import { nextAction } from "@/lib/orderJourney";
import MarkPaidModal from "@/components/MarkPaidModal";

/**
 * Single most-important next step for the current user, pinned at the top of
 * the Order Detail page. Sticky-to-bottom on mobile.
 */
export default function OrderNextAction({ order, role, onChanged }) {
  const action = nextAction(order, role);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [askShip, setAskShip] = useState(false);
  const [askInvoice, setAskInvoice] = useState(false);
  const [askPaid, setAskPaid] = useState(false);

  if (!action) return null;

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); onChanged?.(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const handlePrimary = () => {
    if (!action.action) return;
    if (action.action === "issue_invoice") { setAskInvoice(true); return; }
    if (action.action === "ship") { setAskShip(true); return; }
    if (action.action === "mark_paid") {
      // Always open the same payment-reference form used by the bottom CTA.
      // Never silently POST mark-paid without a reference, method, and
      // (optional) proof attachment.
      setAskPaid(true);
      return;
    }
    if (action.action === "confirm_payment") {
      return run(() => api.post(`/orders/${order.id}/status`, { status: "processing" }));
    }
    if (action.action === "close") {
      return run(() => api.post(`/orders/${order.id}/status`, { status: "closed" }));
    }
    if (action.action === "deliver") {
      return run(() => api.post(`/orders/${order.id}/status`, { status: "delivered" }));
    }
    if (action.action.startsWith("milestone:")) {
      const m = action.action.split(":")[1];
      return run(() => api.post(`/orders/${order.id}/milestone`, { milestone: m }));
    }
  };

  return (
    <>
      <div className="order-next-action" data-testid="order-next-action">
        <div className="ona-head">
          <Sparkles size={14} color="var(--amber-400)" />
          <span className="font-mono ona-tag">NEXT STEP</span>
        </div>
        <div className="ona-title">{action.title}</div>
        <div className="ona-body">{action.body}</div>
        {err && <div className="ona-error" data-testid="ona-error">{err}</div>}
        {action.ctaLabel && (
          <button data-testid="ona-cta" className="btn btn-primary ona-cta" disabled={busy} onClick={handlePrimary}>
            {action.ctaLabel} <ArrowRight size={14} />
          </button>
        )}
      </div>

      {askShip && (
        <ShipModal busy={busy} onCancel={() => setAskShip(false)}
                   onSubmit={(payload) => run(async () => {
                     await api.post(`/orders/${order.id}/milestone`, { milestone: "out_for_delivery", carrier: payload.carrier, eta: payload.eta });
                     await api.post(`/orders/${order.id}/status`, { status: "shipped", tracking_number: payload.tracking });
                     setAskShip(false);
                   })} />
      )}

      {askInvoice && (
        <InvoiceQuickModal orderId={order.id} busy={busy} onCancel={() => setAskInvoice(false)}
                           onSubmit={(payload) => run(async () => {
                             await api.post(`/orders/${order.id}/invoice`, payload);
                             setAskInvoice(false);
                           })} />
      )}

      <MarkPaidModal orderId={order.id} open={askPaid}
                     onClose={() => setAskPaid(false)}
                     onSuccess={onChanged} />
    </>
  );
}

function ShipModal({ onCancel, onSubmit, busy }) {
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const [eta, setEta] = useState("");
  return (
    <div className="modal-backdrop" data-testid="ship-modal" onClick={onCancel}>
      <form onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); onSubmit({ tracking, carrier, eta }); }}
            className="card modal-body">
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".15em", color: "var(--amber-400)", marginBottom: 6 }}>ADD TRACKING &amp; MARK SHIPPED</div>
        <div><label className="label">Carrier</label>
          <input className="input" data-testid="ship-carrier" placeholder="UPS, FedEx, Purolator…" value={carrier} onChange={e => setCarrier(e.target.value)} /></div>
        <div><label className="label">Tracking number *</label>
          <input className="input" data-testid="ship-tracking" required value={tracking} onChange={e => setTracking(e.target.value)} /></div>
        <div><label className="label">Estimated arrival</label>
          <input className="input" type="date" data-testid="ship-eta" value={eta} onChange={e => setEta(e.target.value)} /></div>
        <div className="form-action-row" style={{ marginTop: 10 }}>
          <button data-testid="ship-submit" className="btn btn-primary" disabled={busy}>Mark shipped</button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function InvoiceQuickModal({ orderId, onCancel, onSubmit, busy }) {
  const today = new Date().toISOString().slice(0, 10);
  const [num, setNum] = useState("");
  const [date, setDate] = useState(today);
  const [terms, setTerms] = useState("NET 30");
  const [uploadId, setUploadId] = useState(null);
  const [uploadMeta, setUploadMeta] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const inputRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setUploadErr("File too large (max 10 MB)."); return; }
    setUploadErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("purpose", "invoice"); fd.append("related_id", orderId);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": undefined } });
      setUploadId(data.id);
      setUploadMeta({ name: data.original_filename, size: data.size });
    } catch (err) {
      setUploadErr(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="invoice-quick-modal" onClick={onCancel}>
      <form onClick={e => e.stopPropagation()}
            onSubmit={e => { e.preventDefault(); onSubmit({ supplier_invoice_number: num, supplier_invoice_date: new Date(date).toISOString(), payment_terms: terms, supplier_invoice_upload_id: uploadId || null }); }}
            className="card modal-body">
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".15em", color: "var(--amber-400)", marginBottom: 6 }}>SUBMIT INVOICE</div>
        <div><label className="label">Invoice number *</label>
          <input className="input" data-testid="invq-number" required value={num} onChange={e => setNum(e.target.value)} /></div>
        <div><label className="label">Invoice date *</label>
          <input className="input" type="date" data-testid="invq-date" required value={date} onChange={e => setDate(e.target.value)} /></div>
        <div><label className="label">Payment terms</label>
          <input className="input" data-testid="invq-terms" value={terms} onChange={e => setTerms(e.target.value)} placeholder="NET 30" /></div>
        <div>
          <label className="label">Invoice document (PDF or image)</label>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
            onChange={onPickFile}
            disabled={uploading}
            data-testid="invq-file-input"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          {!uploadId ? (
            <button
              type="button"
              data-testid="invq-file-pick"
              className="btn btn-ghost form-trigger-btn"
              onClick={() => inputRef.current && inputRef.current.click()}
              disabled={uploading}
              style={{ margin: 0 }}
            >
              <Paperclip size={16} /> {uploading ? "Uploading…" : "Attach invoice document"}
            </button>
          ) : (
            <div data-testid="invq-file-attached" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 4, background: "rgba(245,158,11,.05)" }}>
              <FileText size={16} color="var(--amber-400)" />
              <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                <div style={{ color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{uploadMeta?.name || "Invoice attached"}</div>
                {uploadMeta?.size && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{Math.ceil(uploadMeta.size / 1024)} KB</div>}
              </div>
              <button type="button" data-testid="invq-file-clear" className="btn btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} onClick={() => { setUploadId(null); setUploadMeta(null); }}>Replace</button>
            </div>
          )}
          {uploadErr && <div style={{ color: "var(--red)", fontSize: 12, marginTop: 6 }} data-testid="invq-file-error">{uploadErr}</div>}
        </div>
        <div className="form-action-row" style={{ marginTop: 10 }}>
          <button data-testid="invq-submit" className="btn btn-primary" disabled={busy || uploading}>{uploading ? "Uploading…" : "Submit invoice"}</button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
